/**
 * Shared logic for building the template list from feature metrics API (Level 1 and Level 2).
 * Used by both Data Scenarios sidebar and WYSIWYG Templates sidebar so they display the same list.
 */
import { Channel, FeatureResolution, TemplateSummary } from 'types/cx-editor';
import { SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';

export const VALID_FUEL_TYPES = ['ELECTRIC', 'GAS', 'WATER', 'DUAL'];

/**
 * Fuel type sort priority: ELECTRIC > GAS > WATER > DUAL
 * Lower numbers appear first in sorted lists
 */
export const FUEL_TYPE_SORT_ORDER: Record<string, number> = {
  'ELECTRIC': 1,
  'GAS': 2,
  'WATER': 3,
  'DUAL': 4,
};

/**
 * Get the sort priority for a fuel type (lower = higher priority)
 */
export const getFuelTypeSortPriority = (fuelType?: string | null): number => {
  const normalized = normalizeFuelType(fuelType);
  return FUEL_TYPE_SORT_ORDER[normalized] ?? 999;
};

/**
 * Compare two templates by fuel type (primary) and name (secondary)
 */
export const compareTemplatesByFuelAndName = (
  a: TemplateWithSections,
  b: TemplateWithSections
): number => {
  const fuelPriorityA = getFuelTypeSortPriority(a.fuelType);
  const fuelPriorityB = getFuelTypeSortPriority(b.fuelType);

  if (fuelPriorityA !== fuelPriorityB) {
    return fuelPriorityA - fuelPriorityB;
  }

  return a.name.localeCompare(b.name);
};

export interface SectionItem {
  sectionKey: string;
  sectionId: string;
  name: string;
  position?: number | null;
  featureResolution?: FeatureResolution;
}

export interface TemplateWithSections extends TemplateSummary {
  sections: SectionItem[];
  fuelType?: string; // Fuel type for this template (ELECTRIC, GAS, WATER, etc.)
  displayFuelLabel?: string; // Chip label for templates without a fuel type (e.g. 'DF' for USER_WELCOME)
}

export interface ChannelWithTemplates {
  name: Channel;
  templates: TemplateWithSections[];
}

export interface WebPageGroup {
  pageName: string;
  pageDisplayName: string;
  widgets: TemplateWithSections[];
  fuelType?: string; // Fuel type for this page group (used for sorting)
}

export const isValidFuelType = (fuelType?: string | null): boolean => {
  if (!fuelType) return false;
  return VALID_FUEL_TYPES.includes(fuelType.trim().toUpperCase());
};

export const normalizeFuelType = (fuelType?: string | null): string => {
  if (!fuelType) return 'ELECTRIC';
  const normalized = fuelType.trim().toUpperCase();
  return isValidFuelType(normalized) ? normalized : 'ELECTRIC';
};

/**
 * Extract fuel type from a template key.
 * Handles both Email/HER/SMS format (dot-separated) and Web format (double-underscore separated).
 *
 * Returns the fuel type if found, otherwise undefined.
 *
 * Examples:
 * - Email/HER: "EMAIL.DF.MONTHLY_SUMMARY.GAS" → "GAS"
 * - Email (no fuel): "EMAIL.DF.USER_WELCOME" → undefined
 * - Web: "DASHBOARD.HOME.BILL_HISTORY__GAS" → "GAS"
 * - Web (base): "DASHBOARD.HOME.BILL_HISTORY" → undefined
 */
export const getFuelTypeFromTemplateKey = (templateKey: string): string | undefined => {
  if (!templateKey) return undefined;

  // Check for Web format first (double underscore separator)
  // e.g., "DASHBOARD.HOME.BILL_HISTORY__GAS" → "GAS"
  if (templateKey.includes('__')) {
    const parts = templateKey.split('__');
    if (parts.length === 2) {
      const fuelType = parts[1].toUpperCase();
      if (VALID_FUEL_TYPES.includes(fuelType)) {
        return fuelType;
      }
    }
  }

  // Check for Email/HER/SMS format (dot-separated, fuel type as last segment)
  // e.g., "EMAIL.DF.MONTHLY_SUMMARY.GAS" → "GAS"
  const lastPart = templateKey.split('.').pop()?.toUpperCase();
  if (lastPart && VALID_FUEL_TYPES.includes(lastPart)) {
    return lastPart;
  }

  return undefined;
};

export const matchesAnyFuelType = (
  templateFuelType: string | null | undefined,
  allowedFuelTypes: string[]
): boolean => {
  if (!templateFuelType) return true;
  const normalized = normalizeFuelType(templateFuelType);
  return allowedFuelTypes.includes(normalized);
};

export const getFuelTypeLabel = (fuelType?: string | null): string => {
  const n = normalizeFuelType(fuelType);
  switch (n) {
    case 'ELECTRIC':
      return 'EL';
    case 'GAS':
      return 'GAS';
    case 'WATER':
      return 'WATER';
    case 'DUAL':
      return 'DF';
    default:
      return 'EL';
  }
};

const parseL1BaseKey = (featureId: string): string => {
  const parts = featureId.split('.');
  if (parts.length < 2) return featureId;
  const lastPart = parts[parts.length - 1].toUpperCase();
  if (VALID_FUEL_TYPES.includes(lastPart)) {
    return parts.slice(0, -1).join('.');
  }
  return featureId;
};

/**
 * Extract enabled fuel types for Web channel from Level 1 DASHBOARD.* features.
 * Returns fuel types that have ACCESS status (e.g., ['ELECTRIC', 'GAS', 'WATER'])
 *
 * Web features in Level 1 might have fuelType property like Email does,
 * OR they might be separate features like DASHBOARD.ELECTRIC, DASHBOARD.GAS
 */
const extractEnabledWebFuelTypes = (level1Data: FeatureResolution[]): string[] => {
  const enabledFuelTypes = new Set<string>();

  level1Data.forEach(resolution => {
    if (!resolution || resolution.resolution !== 'ACCESS') return;

    const product = resolution.product?.trim().toUpperCase();
    const featureId = resolution.featureId?.toUpperCase() || '';

    // Check if this is a Web/Dashboard feature
    if (product === 'WEB' || featureId.startsWith('DASHBOARD.')) {
      // Method 1: Check if the feature has a fuelType property (like Email does)
      if (resolution.fuelType) {
        const fuelType = typeof resolution.fuelType === 'string'
          ? resolution.fuelType.trim().toUpperCase()
          : String(resolution.fuelType).trim().toUpperCase();
        if (VALID_FUEL_TYPES.includes(fuelType)) {
          enabledFuelTypes.add(fuelType);
        }
      }

      // Method 2: Check if the featureId itself contains the fuel type (e.g., DASHBOARD.ELECTRIC)
      if (featureId.startsWith('DASHBOARD.')) {
        const parts = featureId.split('.');
        if (parts.length >= 2) {
          const potentialFuelType = parts[1];
          if (VALID_FUEL_TYPES.includes(potentialFuelType)) {
            enabledFuelTypes.add(potentialFuelType);
          }
        }
      }
    }
  });

  return Array.from(enabledFuelTypes).sort((a, b) => {
    return getFuelTypeSortPriority(a) - getFuelTypeSortPriority(b);
  });
};

export const getSectionDisplayName = (sectionId: string): string => {
  let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionId];
  if (!displayName) {
    const baseId = sectionId.replace(/_[Vv]\d+$/, '');
    displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[baseId];
  }
  if (!displayName) {
    const cleanId = sectionId.replace(/_[Vv]\d+.*$/, '');
    displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
  }
  if (!displayName) {
    displayName = sectionId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  return displayName;
};

export const extractSmartDisplayName = (featureId: string, displayName?: string | null): string => {
  if (displayName && displayName.length > 2 && displayName.toLowerCase() !== 'df') {
    return displayName;
  }
  const parts = featureId.split('.');
  if (parts.length < 2) return featureId;
  let meaningfulPart = '';
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i].toUpperCase();
    if (VALID_FUEL_TYPES.includes(part) || ['EMAIL', 'WEB', 'HER', 'SMS', 'DF', 'SF'].includes(part)) {
      continue;
    }
    meaningfulPart = parts[i];
    break;
  }
  if (!meaningfulPart) {
    meaningfulPart = parts[parts.length - 1];
  }
  return meaningfulPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Builds a Set of template base keys (without fuel suffix) that have fuel variants in Level 1.
 * This is used to determine which templates are fuel-specific vs no-fuel templates.
 *
 * @param level1Data - Level 1 API feature resolutions
 * @returns Set of base keys for fuel-type templates (e.g., "EMAIL.DF.MONTHLY_SUMMARY")
 *
 * @example
 * Input Level 1:
 *   - EMAIL.DF.MONTHLY_SUMMARY.GAS
 *   - EMAIL.DF.MONTHLY_SUMMARY.WATER
 *   - EMAIL.DF.USER_WELCOME
 *
 * Output Set:
 *   - "EMAIL.DF.MONTHLY_SUMMARY" (has fuel variants)
 *   - NOT "EMAIL.DF.USER_WELCOME" (no fuel suffix)
 */
export function buildFuelTypeTemplateSet(level1Data: FeatureResolution[]): Set<string> {
  const baseKeys = new Set<string>();

  if (!Array.isArray(level1Data) || level1Data.length === 0) {
    return baseKeys;
  }

  level1Data.forEach((resolution) => {
    if (!resolution?.featureId || !resolution?.product) return;

    const product = resolution.product.trim().toUpperCase();
    if (product !== 'EMAIL' && product !== 'SMS' && product !== 'HER') return;

    // Check if this featureId has a fuel suffix using getFuelTypeFromTemplateKey
    const fuelType = getFuelTypeFromTemplateKey(resolution.featureId);
    if (fuelType) {
      // Has fuel suffix - extract base key (remove fuel suffix)
      // e.g., "EMAIL.DF.MONTHLY_SUMMARY.GAS" → "EMAIL.DF.MONTHLY_SUMMARY"
      const parts = resolution.featureId.split('.');
      const baseKey = parts.slice(0, -1).join('.'); // Remove last part (fuel suffix)
      baseKeys.add(baseKey);
    }
  });

  return baseKeys;
}

/**
 * Transforms Level 1 + Level 2 API data into channels with templates.
 * Single source of truth for template list used by Data Scenarios and WYSIWYG sidebars.
 */
export function transformToHierarchicalData(
  level1Data: FeatureResolution[],
  level2Data: FeatureResolution[] | null,
  projectFuelTypes: string[],
  showAllFuelTypes: boolean = false
): ChannelWithTemplates[] {
  if (!Array.isArray(level1Data)) {
    level1Data = [];
  }

  const channelMap = new Map<Channel, Map<string, TemplateWithSections>>();
  const l1BaseKeyToTemplateKey = new Map<string, string[]>();

  const l1AccessResolutions = level1Data.filter(r => {
    if (!r || r.resolution !== 'ACCESS') return false;
    if (showAllFuelTypes) return true;
    if (r.fuelType) {
      const resFuelType =
        typeof r.fuelType === 'string'
          ? r.fuelType.trim().toUpperCase()
          : String(r.fuelType).trim().toUpperCase();
      if (isValidFuelType(resFuelType)) {
        return matchesAnyFuelType(resFuelType, projectFuelTypes);
      }
    }
    return true;
  });

  l1AccessResolutions.forEach(resolution => {
    if (!resolution || !resolution.featureId) return;
    const productValue = resolution.product;
    if (!productValue || typeof productValue !== 'string') return;

    const product = productValue.trim().toUpperCase();
    let channelName: Channel;
    if (product === 'EMAIL') channelName = 'Email';
    else if (product === 'SMS') channelName = 'SMS';
    else if (product === 'WEB') return;
    else if (product === 'HER') return;
    else return;

    const templateKey = resolution.featureId;
    const baseKey = parseL1BaseKey(templateKey);
    const displayName = extractSmartDisplayName(resolution.featureId, resolution.displayName);

    // For Email/SMS templates, derive fuel type from the template key suffix rather than
    // resolution.fuelType. The L1 API may set resolution.fuelType to a project default (e.g.,
    // "ELECTRIC") even for templates like USER_WELCOME that have no fuel-type variant at all.
    // Using the key suffix is authoritative: "EMAIL.DF.MONTHLY_SUMMARY.ELECTRIC" → "ELECTRIC",
    // "EMAIL.DF.USER_WELCOME" → "" (no fuel type).
    const normalizedFuelType = getFuelTypeFromTemplateKey(templateKey) || '';

    // For templates without a fuel type suffix (e.g. EMAIL.DF.USER_WELCOME), derive a display
    // label for the chip from the second segment of the featureId (e.g. "DF").
    let displayFuelLabel: string | undefined;
    if (!normalizedFuelType) {
      const fIdParts = resolution.featureId.split('.');
      if (fIdParts.length >= 2) {
        displayFuelLabel = fIdParts[1].toUpperCase();
      }
    }

    if (!channelMap.has(channelName)) {
      channelMap.set(channelName, new Map());
    }
    const templateMap = channelMap.get(channelName)!;
    if (!templateMap.has(templateKey)) {
      templateMap.set(templateKey, {
        templateKey,
        name: displayName,
        channel: channelName,
        sections: [],
        fuelType: normalizedFuelType,
        displayFuelLabel,
      });
      if (!l1BaseKeyToTemplateKey.has(baseKey)) {
        l1BaseKeyToTemplateKey.set(baseKey, []);
      }
      l1BaseKeyToTemplateKey.get(baseKey)!.push(templateKey);
    }
  });

  if (level2Data && Array.isArray(level2Data)) {
    const l2AccessResolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') return false;
      if (showAllFuelTypes) return true;
      if (r.fuelType) {
        const resFuelType =
          typeof r.fuelType === 'string'
            ? r.fuelType.trim().toUpperCase()
            : String(r.fuelType).trim().toUpperCase();
        if (isValidFuelType(resFuelType)) {
          return matchesAnyFuelType(resFuelType, projectFuelTypes);
        }
      }
      return true;
    });

    l2AccessResolutions.forEach(resolution => {
      const productValue = resolution.product;
      if (!productValue || typeof productValue !== 'string') return;
      const product = productValue.trim().toUpperCase();

      if (product === 'WEB') return;

      let channelName: Channel;
      if (product === 'EMAIL') channelName = 'Email';
      else if (product === 'SMS') channelName = 'SMS';
      else if (product === 'HER') return;
      else return;

      if (!resolution.section) return;

      const sectionKey = resolution.featureId || resolution.section;
      const sectionId = resolution.section;
      const sectionName = getSectionDisplayName(sectionId);
      const section: SectionItem = {
        sectionKey,
        sectionId,
        name: sectionName,
        position: resolution.position,
        featureResolution: resolution,
      };

      if (channelMap.has(channelName) && resolution.productType) {
        const templateMap = channelMap.get(channelName)!;
        let parentTemplate = templateMap.get(resolution.productType);
        if (!parentTemplate) {
          const matchingTemplateKeys = l1BaseKeyToTemplateKey.get(resolution.productType);
          if (matchingTemplateKeys?.length) {
            const l2FuelType = normalizeFuelType(resolution.fuelType);
            for (const key of matchingTemplateKeys) {
              const template = templateMap.get(key);
              if (template && template.fuelType === l2FuelType) {
                parentTemplate = template;
                break;
              }
            }
            if (!parentTemplate) {
              parentTemplate = templateMap.get(matchingTemplateKeys[0]);
            }
          }
        }

        if (parentTemplate) {
          // For templates with no fuel type (e.g., USER_WELCOME), the backend may return
          // Level 2 sections with different fuel types (ELECTRIC, GAS, etc.). These are
          // duplicates of the same section - we should deduplicate by sectionId.
          const isNoFuelTemplate = !parentTemplate.fuelType || parentTemplate.fuelType === '';

          if (isNoFuelTemplate) {
            // Deduplicate by sectionId for no-fuel templates
            const existingSection = parentTemplate.sections.find(s => s.sectionId === sectionId);
            if (!existingSection) {
              parentTemplate.sections.push(section);
            }
          } else {
            // For fuel-type templates, deduplicate by sectionKey (existing logic)
            const existingSection = parentTemplate.sections.find(s => s.sectionKey === sectionKey);
            if (!existingSection) {
              parentTemplate.sections.push(section);
            }
          }
        }
      }
    });
  }

  // Extract enabled fuel types for Web from Level 1 DASHBOARD.* features
  const enabledWebFuelTypes = extractEnabledWebFuelTypes(level1Data);

  // Use Level 2 data for Web templates (only source of truth)
  if (level2Data && Array.isArray(level2Data)) {
    const channelName: Channel = 'Web';
    if (!channelMap.has(channelName)) {
      channelMap.set(channelName, new Map());
    }
    const templateMap = channelMap.get(channelName)!;

    // Filter Level 2 resolutions for Web templates (without fuel type filtering)
    // The fuel type filtering was too strict and rejected valid templates where productType
    // doesn't contain the fuel type string (e.g., "DASHBOARD.HOME.BILL_HISTORY")
    const webL2AccessResolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') return false;
      const productValue = r.product;
      if (!productValue || typeof productValue !== 'string') return false;
      if (productValue.trim().toUpperCase() !== 'WEB') return false;
      if (!r.productType) return false;
      const productTypeUpper = r.productType.toUpperCase();
      // Only check if it starts with DASHBOARD., don't filter by fuel type
      if (!productTypeUpper.startsWith('DASHBOARD.')) return false;
      return true;
    });

    // First pass: identify pages that have widgets (not just standalone entries)
    // This helps us filter out standalone pages when widgets exist for the same page
    const pagesWithWidgets = new Set<string>();
    webL2AccessResolutions.forEach(resolution => {
      const mappedPage = resolution.metadata?.mappedPage;
      const sections = resolution.metadata?.sections;
      if (mappedPage && sections && Array.isArray(sections) && sections.length > 0) {
        pagesWithWidgets.add(mappedPage.toUpperCase());
      }
    });

    // For each enabled fuel type, create template entries
    // We rely on Level 1 data (extractEnabledWebFuelTypes) to determine which fuel types are enabled
    enabledWebFuelTypes.forEach(enabledFuelType => {
      webL2AccessResolutions.forEach(resolution => {
        if (!resolution.featureId) return;

        // Extract mappedPage from metadata (this is the actual page name to use for grouping)
        const mappedPage = resolution.metadata?.mappedPage;
        if (!mappedPage) {
          console.warn('[transformToHierarchicalData] Skipping resolution without mappedPage:', resolution.featureId);
          return;
        }

        // Check if this is a standalone page (no metadata.sections) or a widget page (has metadata.sections)
        const sections = resolution.metadata?.sections;

        let templateKey: string;
        let displayName: string;

        if (!sections || !Array.isArray(sections) || sections.length === 0) {
          // Standalone page (e.g., DASHBOARD.SURVEY.GAS, DASHBOARD.NOTIFICATIONS.GAS)
          // Skip if this page also has widgets - we only want the widgets, not the standalone entry
          if (pagesWithWidgets.has(mappedPage.toUpperCase())) {
            return; // Skip this standalone entry
          }

          // For standalone pages, the productType includes the fuel type suffix
          // We need to strip it to create a consistent template key (e.g., DASHBOARD.SURVEY)

          // Build template key by stripping fuel suffix: DASHBOARD.SURVEY.GAS → DASHBOARD.SURVEY
          templateKey = `DASHBOARD.${mappedPage}`;

          // Get display name from the section field or mappedPage
          const sectionName = resolution.section || mappedPage;
          displayName = getSectionDisplayName(sectionName);
        } else {
          // Widget page (e.g., DASHBOARD.HOME.BILL_PROJECTION)
          // Use the first section from the array as the primary section identifier
          const section = sections[0];

          // Build template key using DASHBOARD.{mappedPage}.{section} format
          // This ensures consistent 3-part naming convention
          templateKey = `DASHBOARD.${mappedPage}.${section}`;
          displayName = getSectionDisplayName(section);
        }

        // Use composite key (templateKey + fuelType) for uniqueness in Map
        // but keep templateKey unchanged for API calls and display
        const compositeKey = `${templateKey}::${enabledFuelType}`; // Use :: as separator for internal tracking

        // If this composite key already exists, skip it (deduplication)
        // This handles cases where the same widget appears with different meterTypes (GB vs GB_MONTH)
        if (!templateMap.has(compositeKey)) {
          templateMap.set(compositeKey, {
            templateKey: templateKey, // Use constructed template key
            name: displayName,
            channel: channelName,
            sections: [],
            fuelType: enabledFuelType,
          });
        }
      });
    });
  }

  const hasHerInLevel1 = l1AccessResolutions.some(r => {
    if (!r || !r.product) return false;
    const product = typeof r.product === 'string' ? r.product.trim().toUpperCase() : '';
    return product === 'HER' && r.resolution === 'ACCESS';
  });

  if (hasHerInLevel1 && level2Data && Array.isArray(level2Data)) {
    const herResolutions = level2Data.filter(r => {
      if (!r || r.resolution !== 'ACCESS') return false;
      const product = r.product?.trim().toUpperCase();
      if (product !== 'HER') return false;
      if (!showAllFuelTypes && r.fuelType) {
        const resFuelType =
          typeof r.fuelType === 'string'
            ? r.fuelType.trim().toUpperCase()
            : String(r.fuelType).trim().toUpperCase();
        if (isValidFuelType(resFuelType)) {
          return matchesAnyFuelType(resFuelType, projectFuelTypes);
        }
      }
      return true;
    });

    const herTemplateMap = new Map<string, TemplateWithSections>();
    herResolutions.forEach(resolution => {
      const featureId = resolution.featureId;
      if (!featureId) return;
      const parts = featureId.split('.');
      if (parts.length < 2) return;

      const templateKey = featureId;
      const fuelTypeFromKey = parts[parts.length - 1]?.toUpperCase() || 'ELECTRIC';
      const normalizedFuelType = normalizeFuelType(fuelTypeFromKey);
      const displayName =
        resolution.displayName || extractSmartDisplayName(featureId, resolution.displayName);

      if (!herTemplateMap.has(templateKey)) {
        herTemplateMap.set(templateKey, {
          templateKey,
          name: displayName,
          channel: 'HER' as Channel,
          sections: [],
          fuelType: normalizedFuelType,
        });
      }

      const template = herTemplateMap.get(templateKey)!;
      const rawData = resolution.featureValueResolutionData;
      if (rawData) {
        try {
          const fallbacks: string[][] =
            typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
          if (Array.isArray(fallbacks)) {
            fallbacks.forEach(fallback => {
              if (Array.isArray(fallback)) {
                fallback.forEach(sectionName => {
                  if (sectionName && typeof sectionName === 'string') {
                    const sectionKey = sectionName;
                    const sectionId = sectionName;
                    const displayName = getSectionDisplayName(sectionId);
                    if (!template.sections.find(s => s.sectionKey === sectionKey)) {
                      template.sections.push({
                        sectionKey,
                        sectionId,
                        name: displayName,
                        position: resolution.position,
                        featureResolution: resolution,
                      });
                    }
                  }
                });
              }
            });
          }
        } catch {
          // ignore parse errors
        }
      }
    });

    if (herTemplateMap.size > 0) {
      const channelName: Channel = 'HER';
      if (!channelMap.has(channelName)) {
        channelMap.set(channelName, new Map());
      }
      const templateMap = channelMap.get(channelName)!;
      herTemplateMap.forEach((template, key) => {
        template.sections.sort((a, b) => {
          if (
            a.position != null &&
            b.position != null &&
            a.position !== undefined &&
            b.position !== undefined
          ) {
            return a.position - b.position;
          }
          return a.name.localeCompare(b.name);
        });
        templateMap.set(key, template);
      });
    }
  }

  const channelOrder: Channel[] = ['Email', 'HER', 'SMS', 'Web'];
  const result = channelOrder
    .filter(ch => channelMap.has(ch))
    .map(ch => {
      const templateMap = channelMap.get(ch)!;
      const templates = Array.from(templateMap.values())
        .map(t => ({
          ...t,
          sections: t.sections.sort((a, b) => {
            if (
              a.position != null &&
              b.position != null &&
              a.position !== undefined &&
              b.position !== undefined
            ) {
              return a.position - b.position;
            }
            return a.name.localeCompare(b.name);
          }),
        }))
        .sort(compareTemplatesByFuelAndName);
      return { name: ch, templates };
    })
    .filter(ch => ch.templates.length > 0);

  return result;
}

/**
 * Parse a Web template key to extract page and widget information.
 * Example: "DASHBOARD.HOME.BILL_PROJECTION" → { page: "HOME", widget: "BILL_PROJECTION", displayName: "Bill Projection" }
 * Example: "DASHBOARD.SURVEY" → { page: "SURVEY", widget: "SURVEY", displayName: "Survey" }
 */
export function parseWebTemplateKey(templateKey: string): { page: string; widget: string; displayName: string } | null {
  if (!templateKey || !templateKey.startsWith('DASHBOARD.')) {
    return null;
  }

  const parts = templateKey.split('.');
  if (parts.length < 2) {
    return null;
  }

  // Handle standalone pages (2 parts): DASHBOARD.{PAGE}
  if (parts.length === 2) {
    const page = parts[1];
    const displayName = page
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    return { page, widget: page, displayName };
  }

  // Handle widget pages (3+ parts): DASHBOARD.{PAGE}.{WIDGET} or DASHBOARD.{PAGE}.{SUBPAGE}.{WIDGET}
  const page = parts[1];
  const widget = parts.slice(2).join('.');

  // Convert widget name to display name (e.g., "BILL_PROJECTION" → "Bill Projection")
  const displayName = widget
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return { page, widget, displayName };
}



/**
 * Get a human-readable display name for a page.
 * Example: "HOME" → "Home", "ENERGY_INSIGHTS" → "Energy Insights"
 */
export function getPageDisplayName(pageName: string): string {
  return pageName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Group Web templates by their parent page AND fuel type.
 * Transforms flat list into hierarchical Page → Widgets structure.
 * When multiple fuel types exist, creates separate page groups for each fuel type.
 */
export function groupWebTemplatesByPage(templates: TemplateWithSections[]): WebPageGroup[] {
  // Group by page + fuel type combination
  const pageMap = new Map<string, TemplateWithSections[]>();

  templates.forEach(template => {
    const parsed = parseWebTemplateKey(template.templateKey);
    if (!parsed) {
      // If it doesn't match the pattern, put it in an "Other" group
      const otherKey = `OTHER__${template.fuelType || 'UNKNOWN'}`;
      if (!pageMap.has(otherKey)) {
        pageMap.set(otherKey, []);
      }
      pageMap.get(otherKey)!.push(template);
      return;
    }

    // Create a unique key combining page name and fuel type
    const fuelType = template.fuelType || 'UNKNOWN';
    const pageKey = `${parsed.page}__${fuelType}`;

    if (!pageMap.has(pageKey)) {
      pageMap.set(pageKey, []);
    }
    pageMap.get(pageKey)!.push(template);
  });

  // Convert map to array and sort pages
  const pageGroups: WebPageGroup[] = Array.from(pageMap.entries()).map(([pageKey, widgets]) => {
    // Extract page name and fuel type from the combined key (e.g., "HOME__ELECTRIC" → "HOME", "ELECTRIC")
    const parts = pageKey.split('__');
    const pageName = parts[0];
    const fuelType = parts[1] || 'UNKNOWN';

    return {
      pageName,
      pageDisplayName: getPageDisplayName(pageName),
      widgets: widgets.sort(compareTemplatesByFuelAndName),
      fuelType // Store fuel type for sorting
    };
  });

  // Sort pages with two-level sort:
  // 1. Primary: Group by fuel type (ELECTRIC, GAS, WATER, etc.)
  // 2. Secondary: Within each fuel type, sort by page name (HOME first, then alphabetically, OTHER last)
  pageGroups.sort((a, b) => {
    // Primary sort: by fuel type
    const fuelPriorityA = getFuelTypeSortPriority(a.fuelType);
    const fuelPriorityB = getFuelTypeSortPriority(b.fuelType);

    if (fuelPriorityA !== fuelPriorityB) {
      return fuelPriorityA - fuelPriorityB;
    }

    // Secondary sort: by page name (within same fuel type)
    if (a.pageName === 'HOME') return -1;
    if (b.pageName === 'HOME') return 1;
    if (a.pageName === 'OTHER') return 1;
    if (b.pageName === 'OTHER') return -1;
    return a.pageDisplayName.localeCompare(b.pageDisplayName);
  });

  return pageGroups;
}
